/**
 * Authoritative LOI lifecycle statuses.
 *
 * Deliberately kept separate from `v2/helpers/status/orders` so that the
 * existing order status switches can never receive an LOI status.
 */
const DRAFT = 'draft';
const AWAITING_APPROVAL = 'awaiting_approval';
const APPROVAL_REJECTED = 'approval_rejected';
const APPROVED = 'approved';
const SENDING = 'sending';
const SEND_FAILED = 'send_failed';
const AWAITING_SIGNATURE = 'awaiting_signature';
const SIGNATURE_DECLINED = 'signature_declined';
const WITHDRAWN = 'withdrawn';
const VOIDED = 'voided';
const EXECUTED = 'executed';
const EXPIRED = 'expired';

/**
 * Tone drives the badge colour. It maps onto the same four tones already used
 * by the approver status badges, so LOI statuses sit visually alongside them.
 */
const TONE = {
  NEUTRAL: 'neutral',
  PENDING: 'pending',
  SUCCESS: 'success',
  ERROR: 'error',
};

/**
 * uid -> { labelKey, tone }
 *
 * `labelKey` is an i18n key; a mapped headline is always exactly one of these
 * labels, and the approver count stays in the expandable panel below it. The
 * sole composite is the unmapped "Pending Signature (1/2)" label, whose count
 * is of signatories - which no panel shows. See `withSignatureProgress`.
 */
const LOI_STATUS_CONFIG = {
  [DRAFT]: { labelKey: 'loi-status-draft', tone: TONE.NEUTRAL },
  [AWAITING_APPROVAL]: {
    labelKey: 'loi-status-awaiting-approval',
    tone: TONE.PENDING,
  },
  [APPROVAL_REJECTED]: {
    labelKey: 'loi-status-approval-rejected',
    tone: TONE.ERROR,
  },
  [APPROVED]: { labelKey: 'loi-status-approved', tone: TONE.SUCCESS },
  [SENDING]: { labelKey: 'loi-status-processing', tone: TONE.PENDING },
  [SEND_FAILED]: { labelKey: 'loi-status-send-failed', tone: TONE.ERROR },
  [AWAITING_SIGNATURE]: {
    labelKey: 'loi-status-awaiting-signature',
    tone: TONE.PENDING,
  },
  [SIGNATURE_DECLINED]: {
    labelKey: 'loi-status-signature-declined',
    tone: TONE.ERROR,
  },
  [WITHDRAWN]: { labelKey: 'loi-status-withdrawn', tone: TONE.NEUTRAL },
  [VOIDED]: { labelKey: 'loi-status-voided', tone: TONE.NEUTRAL },
  [EXECUTED]: { labelKey: 'loi-status-executed', tone: TONE.SUCCESS },
  [EXPIRED]: { labelKey: 'loi-status-expired', tone: TONE.ERROR },
};

const LOI_STATUSES = Object.keys(LOI_STATUS_CONFIG);

/**
 * Statuses that have already reached a definitive end and must not be
 * re-labelled as Expired once the expiry date passes.
 *
 * Executed is the case the ticket calls out explicitly. Withdrawn, Voided and
 * Signature Declined are included because showing "Expired" for an LOI the
 * issuer already withdrew hides what actually happened.
 */
const TERMINAL_STATUSES = [
  EXECUTED,
  EXPIRED,
  WITHDRAWN,
  VOIDED,
  SIGNATURE_DECLINED,
];

const parseApiDate = (value) => {
  if (value instanceof Date) return value;
  if (typeof value !== 'string') return null;

  const parsed = new Date(value.trim().replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Expiry is time-based only: it depends on the LOI expiry date captured in
 * Tender Recommendation and nothing else. It never applies to a terminal
 * status, so an Executed LOI stays Executed forever.
 *
 * @param {string} status mapped status uid
 * @param {string|Date|null} expiryDate
 * @param {Date} [now]
 * @returns {string} the status to display
 */
const resolveLoiStatus = (status, expiryDate, now = new Date()) => {
  if (!status || TERMINAL_STATUSES.includes(status)) return status;

  const expiry = parseApiDate(expiryDate);
  if (!expiry) return status;

  return expiry < now ? EXPIRED : status;
};

const API_STATUS_MAP = {
  Draft: DRAFT,
  'Pending Approval': AWAITING_APPROVAL,
  Processing: SENDING,
  Rejected: APPROVAL_REJECTED,
  Expired: EXPIRED,
  Signed: EXECUTED,
};

const API_STATUS_SIGNED = 'Signed';

/**
 * The API label for an LOI that has been sent out and is waiting on its
 * signatories. Deliberately absent from API_STATUS_MAP, so it reaches the
 * status cell as a raw label - which is what carries the signature count.
 */
const API_STATUS_PENDING_SIGNATURE = 'Pending Signature';

const mapApiLoiStatus = (apiStatus) => {
  if (apiStatus === null || apiStatus === undefined || apiStatus === '') {
    return { uid: DRAFT, rawLabel: null };
  }

  const uid = API_STATUS_MAP[apiStatus] ?? null;
  return { uid, rawLabel: uid ? null : String(apiStatus) };
};

/**
 * Is this LOI fully signed?
 *
 * Checked against the raw API status rather than the mapped uid, because the
 * uid is a display concern and this is a business gate.
 *
 * @param {string|null} apiStatus the raw `entries[].status`
 */
const isLoiSigned = (apiStatus) => apiStatus === API_STATUS_SIGNED;

const toCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? count : null;
};

/**
 * "signed/total" for an entry's `signatory` summary, or null when there are
 * no signatories to count. A missing `signers` reads as none signed yet,
 * which is what the API sends before the first signature.
 *
 * @param {{total: number, signers: number}} [signatory] `entry.signatory`
 * @returns {string|null}
 */
const formatSignatureProgress = (signatory) => {
  const total = toCount(signatory?.total);
  if (!total) return null;

  return `${toCount(signatory?.signers) ?? 0}/${total}`;
};

/**
 * Appends the signature progress to the Pending Signature label, e.g.
 * "Pending Signature (1/2)", so the cell says how many signatories are still
 * outstanding. Mirrors what the order rows already show. Every other label is
 * returned untouched.
 *
 * @param {string|null} rawLabel the raw `entries[].status`
 * @param {{total: number, signers: number}} [signatory] `entry.signatory`
 * @returns {string|null}
 */
const withSignatureProgress = (rawLabel, signatory) => {
  if (rawLabel !== API_STATUS_PENDING_SIGNATURE) return rawLabel;

  const progress = formatSignatureProgress(signatory);
  return progress ? `${rawLabel} (${progress})` : rawLabel;
};

export {
  DRAFT,
  AWAITING_APPROVAL,
  APPROVAL_REJECTED,
  APPROVED,
  SENDING,
  SEND_FAILED,
  AWAITING_SIGNATURE,
  SIGNATURE_DECLINED,
  WITHDRAWN,
  VOIDED,
  EXECUTED,
  EXPIRED,
  TONE,
  LOI_STATUS_CONFIG,
  LOI_STATUSES,
  TERMINAL_STATUSES,
  API_STATUS_MAP,
  API_STATUS_SIGNED,
  API_STATUS_PENDING_SIGNATURE,
  mapApiLoiStatus,
  isLoiSigned,
  formatSignatureProgress,
  withSignatureProgress,
  parseApiDate,
  resolveLoiStatus,
};
